import React, { useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Grid,
  Alert,
  Chip,
} from '@mui/material';
import { Email, Chat, WhatsApp } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { RootState } from '../store/index';
import { apiService } from '../services/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AgencyInterestSection } from '../components/preferences/AgencyInterestSection';
import { NotificationMonitoringSection } from '../components/preferences/NotificationMonitoringSection';
import { AgencyMonitoringSuiteDialogs } from '../components/monitoring';
import { useActiveCollectorAgencies } from '../hooks/useActiveCollectorAgencies';
import { useAgencyMonitoringSuite } from '../hooks/useAgencyMonitoringSuite';
import {
  PreferencesMonitoringKpiRow,
  type PreferencesKpiSection,
} from '../components/preferences/PreferencesMonitoringKpiRow';

export function PreferencesPage() {
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = user?.id || 1;

  const interestSectionRef = useRef<HTMLDivElement>(null);
  const monitoringSectionRef = useRef<HTMLDivElement>(null);

  const [activeKpi, setActiveKpi] = useState<PreferencesKpiSection | null>(null);
  const [monitoringTab, setMonitoringTab] = useState<'assuntos' | 'agencies'>('assuntos');
  const agencyMonitoring = useAgencyMonitoringSuite();

  const { data: preferences, isLoading } = useQuery(
    ['preferences', userId],
    () => apiService.getPreferences(userId),
    { enabled: !!user?.id },
  );

  const { count: activeCollectorCount, isLoading: activeCollectorLoading } = useActiveCollectorAgencies({
    enabled: !!user?.id,
  });

  const { data: assuntosMonitoring, isLoading: assuntosLoading } = useQuery(
    ['assuntos-monitoring'],
    () => apiService.getAssuntosMonitoring(),
    { enabled: !!user?.id, staleTime: 30_000 },
  );

  const { data: agenciesMonitoringHistory, isLoading: agenciesLoading } = useQuery(
    ['agencies-monitoring-history'],
    () => apiService.getAgenciesMonitoringHistory(),
    { enabled: !!user?.id, staleTime: 30_000 },
  );

  const emailChannelActive = preferences?.channels?.includes('email') ?? true;

  const monitoredAssuntosCount =
    assuntosMonitoring?.assuntos?.filter((a) => a.is_monitored).length ?? 0;
  const monitoredAgenciesCount =
    agenciesMonitoringHistory?.agencies?.filter((a) => a.is_monitored).length ?? 0;

  const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoading) {
    return <LoadingSpinner message="Carregando preferências..." />;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Preferências de Notificação
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Coleta ativa no sistema e suas escolhas de monitoramento com notificações.
      </Typography>

      <PreferencesMonitoringKpiRow
        agencyInterestCount={activeCollectorCount}
        agencyInterestLoading={activeCollectorLoading}
        assuntosCount={monitoredAssuntosCount}
        assuntosLoading={assuntosLoading}
        agenciesCount={monitoredAgenciesCount}
        agenciesLoading={agenciesLoading}
        activeSection={activeKpi}
        onAgencyInterestClick={() => {
          setActiveKpi('interest');
          scrollToRef(interestSectionRef);
        }}
        onAssuntosClick={() => {
          setActiveKpi('assuntos');
          setMonitoringTab('assuntos');
          scrollToRef(monitoringSectionRef);
        }}
        onAgenciesClick={() => {
          setActiveKpi('agencies');
          setMonitoringTab('agencies');
          scrollToRef(monitoringSectionRef);
          agencyMonitoring.openAgencyMonitoring();
        }}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} ref={interestSectionRef}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Agências de interesse
              </Typography>
              <AgencyInterestSection />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} ref={monitoringSectionRef}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Monitoramento que gera notificações
              </Typography>
              <NotificationMonitoringSection
                tab={monitoringTab}
                onTabChange={setMonitoringTab}
                onOpenAgencyMonitoring={agencyMonitoring.openAgencyMonitoring}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Canais de Notificação
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Hoje as notificações automáticas por monitoramento são enviadas por <strong>e-mail (D+1)</strong>.
                Slack e WhatsApp estão <strong>em breve</strong>.
              </Alert>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={emailChannelActive}
                      disabled
                      icon={<Email />}
                      checkedIcon={<Email />}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        E-mail
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ativo — notificações de monitoramento
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  disabled
                  control={<Checkbox disabled checked={false} icon={<Chat />} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.disabled' }}>
                          Slack
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Canal #legislacao
                        </Typography>
                      </Box>
                      <Chip label="Em breve" size="small" variant="outlined" />
                    </Box>
                  }
                />

                <FormControlLabel
                  disabled
                  control={<Checkbox disabled checked={false} icon={<WhatsApp />} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.disabled' }}>
                          WhatsApp
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Mensagens diretas
                        </Typography>
                      </Box>
                      <Chip label="Em breve" size="small" variant="outlined" />
                    </Box>
                  }
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Frequência de Envio
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Resumos diários, semanais ou mensais por preferência estão <strong>em breve</strong>. O envio atual
                é automático por mudança monitorada (D+1).
              </Alert>

              <FormControl component="fieldset" sx={{ opacity: 0.55, pointerEvents: 'none' }}>
                <RadioGroup value={preferences?.frequency ?? 'daily'}>
                  <FormControlLabel value="daily" control={<Radio />} label="Diário" />
                  <FormControlLabel value="weekly" control={<Radio />} label="Semanal" />
                  <FormControlLabel value="monthly" control={<Radio />} label="Mensal" />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <AgencyMonitoringSuiteDialogs {...agencyMonitoring} />
    </Box>
  );
}
